// src/services/training.service.ts
import { db, storage } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  increment,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Course,
  CourseStatus,
  CourseModule,
  CourseAssignment,
  CourseProgress,
  ModuleProgress,
  Certificate,
  TrainingDocument,
  ProgressStatus
} from '../types/training.types';

export const trainingService = {
  async createCourse(
    courseData: Omit<Course, 'id' | 'createdAt' | 'updatedAt' | 'enrollmentCount' | 'completionCount'>
  ): Promise<string> {
    const courseRef = doc(collection(db, 'courses'));
    const course: Course = {
      ...courseData,
      id: courseRef.id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      enrollmentCount: 0,
      completionCount: 0
    };

    await setDoc(courseRef, course);
    return courseRef.id;
  },

  async getCourse(courseId: string): Promise<Course | null> {
    const docRef = doc(db, 'courses', courseId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as Course;
    }
    return null;
  },

  async getOrganizationCourses(organizationId: string, status?: CourseStatus): Promise<Course[]> {
    let q = query(
      collection(db, 'courses'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    let courses = snapshot.docs.map(doc => doc.data() as Course);

    if (status) {
      courses = courses.filter(c => c.status === status);
    }

    return courses;
  },

  async getAllCourses(status?: CourseStatus): Promise<Course[]> {
    const q = query(
      collection(db, 'courses'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    let courses = snapshot.docs.map(doc => doc.data() as Course);

    if (status) {
      courses = courses.filter(c => c.status === status);
    }

    return courses;
  },

  async updateCourse(courseId: string, updates: Partial<Course>): Promise<void> {
    await updateDoc(doc(db, 'courses', courseId), {
      ...updates,
      updatedAt: Timestamp.now()
    });
  },

  async publishCourse(courseId: string): Promise<void> {
    await updateDoc(doc(db, 'courses', courseId), {
      status: 'published',
      publishedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  },

  async deleteCourse(courseId: string): Promise<{ assignmentsDeleted: number; progressDeleted: number }> {
    let assignmentsDeleted = 0;
    let progressDeleted = 0;

    // Delete all course assignments for this course
    const assignmentsQuery = query(
      collection(db, 'courseAssignments'),
      where('courseId', '==', courseId)
    );
    const assignmentsSnapshot = await getDocs(assignmentsQuery);

    if (assignmentsSnapshot.docs.length > 0) {
      const batch = writeBatch(db);
      assignmentsSnapshot.docs.forEach(docSnap => {
        batch.delete(docSnap.ref);
        assignmentsDeleted++;
      });
      await batch.commit();
    }

    // Delete all course progress records for this course
    const progressQuery = query(
      collection(db, 'courseProgress'),
      where('courseId', '==', courseId)
    );
    const progressSnapshot = await getDocs(progressQuery);

    if (progressSnapshot.docs.length > 0) {
      const batch = writeBatch(db);
      progressSnapshot.docs.forEach(docSnap => {
        batch.delete(docSnap.ref);
        progressDeleted++;
      });
      await batch.commit();
    }

    // Delete the course document itself
    await deleteDoc(doc(db, 'courses', courseId));

    return { assignmentsDeleted, progressDeleted };
  },

  async assignCourse(
    courseId: string,
    courseName: string,
    userId: string,
    userName: string,
    assignedBy: string,
    assignedByName: string,
    dueDate?: Date,
    required = true
  ): Promise<string> {
    const assignmentRef = doc(collection(db, 'courseAssignments'));
    const assignment: CourseAssignment = {
      id: assignmentRef.id,
      courseId,
      courseName,
      userId,
      userName,
      assignedBy,
      assignedByName,
      assignedAt: Timestamp.now(),
      dueDate: dueDate ? Timestamp.fromDate(dueDate) : undefined,
      status: 'not_started',
      required
    };

    await setDoc(assignmentRef, assignment);

    // Increment enrollment count
    await updateDoc(doc(db, 'courses', courseId), {
      enrollmentCount: increment(1)
    });

    return assignmentRef.id;
  },

  async getUserAssignments(userId: string): Promise<CourseAssignment[]> {
    const q = query(
      collection(db, 'courseAssignments'),
      where('userId', '==', userId),
      orderBy('assignedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as CourseAssignment);
  },

  async startCourse(
    userId: string,
    userName: string,
    courseId: string,
    courseName: string
  ): Promise<CourseProgress> {
    const progressId = `${userId}_${courseId}`;
    const progressRef = doc(db, 'courseProgress', progressId);
    const existing = await getDoc(progressRef);

    if (existing.exists()) {
      // Update last accessed
      await updateDoc(progressRef, {
        lastAccessedAt: Timestamp.now()
      });
      return existing.data() as CourseProgress;
    }

    const course = await this.getCourse(courseId);
    if (!course) throw new Error('Course not found');

    const moduleProgress: ModuleProgress[] = course.modules.map(m => ({
      moduleId: m.id,
      status: 'not_started'
    }));

    const progress: CourseProgress = {
      id: progressId,
      userId,
      userName,
      courseId,
      courseName,
      status: 'in_progress',
      moduleProgress,
      overallProgress: 0,
      startedAt: Timestamp.now(),
      lastAccessedAt: Timestamp.now()
    };

    await setDoc(progressRef, progress);

    // Update assignment status
    const assignmentsQuery = query(
      collection(db, 'courseAssignments'),
      where('userId', '==', userId),
      where('courseId', '==', courseId)
    );
    const assignmentsSnapshot = await getDocs(assignmentsQuery);
    for (const doc of assignmentsSnapshot.docs) {
      await updateDoc(doc.ref, { status: 'in_progress' });
    }

    return progress;
  },

  async getCourseProgress(userId: string, courseId: string): Promise<CourseProgress | null> {
    const progressId = `${userId}_${courseId}`;
    const docRef = doc(db, 'courseProgress', progressId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as CourseProgress;
    }
    return null;
  },

  async updateModuleProgress(
    userId: string,
    courseId: string,
    moduleId: string,
    updates: Partial<ModuleProgress>
  ): Promise<void> {
    const progressId = `${userId}_${courseId}`;
    const progressRef = doc(db, 'courseProgress', progressId);
    const progress = await getDoc(progressRef);

    if (!progress.exists()) {
      throw new Error('Progress not found');
    }

    const data = progress.data() as CourseProgress;
    const moduleProgress = data.moduleProgress.map(mp =>
      mp.moduleId === moduleId ? { ...mp, ...updates } : mp
    );

    // Calculate overall progress
    const completedModules = moduleProgress.filter(mp => mp.status === 'completed').length;
    const overallProgress = Math.round((completedModules / moduleProgress.length) * 100);

    // Check if course is complete
    const allComplete = moduleProgress.every(mp => mp.status === 'completed');

    await updateDoc(progressRef, {
      moduleProgress,
      overallProgress,
      status: allComplete ? 'completed' : 'in_progress',
      lastAccessedAt: Timestamp.now(),
      ...(allComplete ? { completedAt: Timestamp.now() } : {})
    });

    // Update assignment and course stats if completed
    if (allComplete) {
      await updateDoc(doc(db, 'courses', courseId), {
        completionCount: increment(1)
      });

      const assignmentsQuery = query(
        collection(db, 'courseAssignments'),
        where('userId', '==', userId),
        where('courseId', '==', courseId)
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      for (const doc of assignmentsSnapshot.docs) {
        await updateDoc(doc.ref, {
          status: 'completed',
          completedAt: Timestamp.now()
        });
      }
    }
  },

  async recordQuizAttempt(
    userId: string,
    courseId: string,
    moduleId: string,
    score: number,
    passed: boolean,
    answers: Record<string, string | string[]>
  ): Promise<void> {
    const progress = await this.getCourseProgress(userId, courseId);
    if (!progress) throw new Error('Progress not found');

    const moduleProgress = progress.moduleProgress.find(mp => mp.moduleId === moduleId);
    if (!moduleProgress) throw new Error('Module not found');

    const attempts = moduleProgress.quizAttempts || [];
    attempts.push({
      attemptNumber: attempts.length + 1,
      score,
      passed,
      answers,
      completedAt: Timestamp.now()
    });

    await this.updateModuleProgress(userId, courseId, moduleId, {
      quizAttempts: attempts,
      ...(passed ? { status: 'completed', completedAt: Timestamp.now() } : {})
    });
  },

  async generateCertificate(
    userId: string,
    userName: string,
    userEmail: string,
    courseId: string,
    courseName: string,
    organizationId: string,
    organizationName: string
  ): Promise<Certificate> {
    const certRef = doc(collection(db, 'certificates'));
    const verificationCode = `CERT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

    const certificate: Certificate = {
      id: certRef.id,
      userId,
      userName,
      userEmail,
      courseId,
      courseName,
      issuedAt: Timestamp.now(),
      verificationCode,
      organizationId,
      organizationName
    };

    await setDoc(certRef, certificate);

    // Update progress with certificate ID
    const progressId = `${userId}_${courseId}`;
    await updateDoc(doc(db, 'courseProgress', progressId), {
      certificateId: certRef.id
    });

    return certificate;
  },

  async getUserCertificates(userId: string): Promise<Certificate[]> {
    const q = query(
      collection(db, 'certificates'),
      where('userId', '==', userId),
      orderBy('issuedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as Certificate);
  },

  async uploadDocument(
    file: File,
    organizationId: string,
    title: string,
    category: string,
    uploadedBy: string,
    uploadedByName: string,
    description?: string,
    tags?: string[]
  ): Promise<string> {
    const docRef = doc(collection(db, 'trainingDocuments'));
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const storageRef = ref(storage, `training/${organizationId}/${fileName}`);

    await uploadBytes(storageRef, file);
    const fileUrl = await getDownloadURL(storageRef);

    const document: TrainingDocument = {
      id: docRef.id,
      organizationId,
      title,
      description,
      category,
      fileUrl,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      uploadedBy,
      uploadedByName,
      uploadedAt: Timestamp.now(),
      tags
    };

    await setDoc(docRef, document);
    return docRef.id;
  },

  async getDocuments(organizationId: string, category?: string): Promise<TrainingDocument[]> {
    let q = query(
      collection(db, 'trainingDocuments'),
      where('organizationId', '==', organizationId),
      orderBy('uploadedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    let docs = snapshot.docs.map(doc => doc.data() as TrainingDocument);

    if (category) {
      docs = docs.filter(d => d.category === category);
    }

    return docs;
  },

  /**
   * Assign all published courses to a new user
   * Called during user onboarding
   */
  async assignRequiredCoursesToUser(
    userId: string,
    userName: string,
    organizationId: string,
    assignedBy: string,
    assignedByName: string
  ): Promise<string[]> {
    const assignedCourseIds: string[] = [];

    try {
      // Get all published courses for this organization. (There is no
      // getCourses(orgId) — getAllCourses + an org filter is the real API;
      // the phantom method call broke every course auto-assign on user create.)
      const courses = await this.getAllCourses('published');
      const publishedCourses = courses.filter(
        c => !c.organizationId || c.organizationId === organizationId
      );

      for (const course of publishedCourses) {
        try {
          // Check if user already has this course assigned
          const existingAssignments = await this.getUserAssignments(userId);
          const alreadyAssigned = existingAssignments.some(a => a.courseId === course.id);

          if (!alreadyAssigned) {
            const assignmentId = await this.assignCourse(
              course.id,
              course.title,
              userId,
              userName,
              assignedBy,
              assignedByName,
              undefined, // No due date
              true // Required
            );
            assignedCourseIds.push(assignmentId);
          }
        } catch (error) {
          console.error(`Failed to assign course ${course.id} to user ${userId}:`, error);
          // Continue with other courses even if one fails
        }
      }

      return assignedCourseIds;
    } catch (error) {
      console.error('Error assigning required courses:', error);
      return assignedCourseIds;
    }
  }
};
